import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useCallback } from 'react';

export interface Stats {
  internal: {
    commits: number;
    devs: number;
  };
  external: {
    commits: number;
    devs: number;
  };
}

export interface AggregateStats {
  [key: string]: Stats;
}

export interface ProjectsRevenue {
  total: number;
  average: number;
}
export interface CodeownersModelInterface {
    ref: string;
    email?: string;
    login?: string;
    team?: string;
}

const fetchStatistics = async <T>({
  baseUrl,
  token,
  pathname,
}: {
  baseUrl: string;
  token?: string;
  pathname: string;
}): Promise<T> => {
  const response = await fetch(`${baseUrl}/${pathname}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.json();
};

export function useGithubStatisticsForTeam(): (ref: string) => Promise<CodeownersModelInterface[]> {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const fetchResponse = useCallback(async (ref: string) => {
    const baseUrl = await discoveryApi.getBaseUrl('github-statistics');
    const path = `codeowners/${ref}`;
    const { token } = await identityApi.getCredentials();

    return fetchStatistics<CodeownersModelInterface[]>({
      baseUrl,
      token,
      pathname: path,
    });
  }, [discoveryApi, identityApi]);

  return fetchResponse;
}

export function useGithubStatisticsForComponent(): (
  ref: string,
) => Promise<AggregateStats> {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const fetchResponse = useCallback(
    async (ref: string) => {
      const baseUrl = await discoveryApi.getBaseUrl('github-statistics');
      const path = `commits/${ref}`;
      const { token } = await identityApi.getCredentials();

      return fetchStatistics<AggregateStats>({
        baseUrl,
        token,
        pathname: path,
      });
    },
    [discoveryApi, identityApi],
  );

  return fetchResponse;
}

export function useGithubStatisticsForProjects(): () => Promise<AggregateStats> {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const fetchResponse = useCallback(async () => {
    const baseUrl = await discoveryApi.getBaseUrl('github-statistics');
    const path = `projects`;
    const { token } = await identityApi.getCredentials();

    return fetchStatistics<AggregateStats>({
      baseUrl,
      token,
      pathname: path,
    });
  }, [discoveryApi, identityApi]);

  return fetchResponse;
}

export function useGithubStatisticsRevenue(): () => Promise<ProjectsRevenue> {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const fetchResponse = useCallback(async () => {
    const baseUrl = await discoveryApi.getBaseUrl('github-statistics');
    const path = `projects/revenue`;
    const { token } = await identityApi.getCredentials();

    return fetchStatistics<ProjectsRevenue>({
      baseUrl,
      token,
      pathname: path,
    });
  }, [discoveryApi, identityApi]);

  return fetchResponse;
}
